import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Users, UserCog, Building2 } from "lucide-react";

const TEST_ACCOUNTS = [
  {
    role: "Collaborateur",
    description: "Consulter ses conges, faire une demande",
    email: "newsletter@verbaereauto.com",
    password: "adminpassword",
    icon: Users,
    color: "bg-[#1a2634]",
    hover: "hover:bg-[#1a2634]/90",
  },
  {
    role: "Manager",
    description: "Valider les demandes, voir l'equipe",
    email: "verbaere@verbaereauto.com",
    password: "adminpassword",
    icon: UserCog,
    color: "bg-[#E53935]",
    hover: "hover:bg-[#C62828]",
  },
  {
    role: "RH / Admin",
    description: "Gestion complete, stats, export, admin",
    email: "admin@verbaere.com",
    password: "adminpassword",
    icon: Building2,
    color: "bg-[#00A3E0]",
    hover: "hover:bg-[#0090c5]",
  },
];

export default function LoginPage() {
  const [loading, setLoading] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleQuickLogin = async (account) => {
    setLoading(account.role);
    try {
      const user = await login(account.email, account.password);
      toast.success(`Bienvenue, ${user.first_name} !`);
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur de connexion");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Role selection */}
      <div className="w-full md:w-1/2 flex flex-col justify-center px-8 md:px-16 lg:px-24 bg-white">
        <div className="max-w-md w-full mx-auto">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-md border border-gray-100">
              <img 
                src="/logo-pitstop.png" 
                alt="Pitstop" 
                className="h-8 w-8 object-contain"
              />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1a2634]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Pitstop
              </h1>
              <p className="text-xs text-gray-400">by Verbaere Performance Suite</p>
            </div>
          </div>

          {/* Title */}
          <h2 className="text-3xl font-bold text-[#1a2634] mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Connexion rapide
          </h2>
          <p className="text-gray-500 mb-8">
            Choisissez un profil pour acceder a l'application
          </p>

          {/* Role cards */}
          <div className="space-y-3">
            {TEST_ACCOUNTS.map((account) => {
              const Icon = account.icon;
              const isLoading = loading === account.role;
              return (
                <Button
                  key={account.role}
                  onClick={() => handleQuickLogin(account)}
                  disabled={!!loading}
                  className={`w-full h-auto py-5 px-6 ${account.color} ${account.hover} text-white rounded-xl flex items-center gap-4 justify-start transition-all duration-200 shadow-md hover:shadow-lg hover:-translate-y-0.5`}
                  data-testid={`login-${account.role.toLowerCase().replace(/[\s\/]/g, '-')}`}
                >
                  {isLoading ? (
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                      <Icon className="h-5 w-5" />
                    </div>
                  )}
                  <div className="text-left">
                    <p className="font-semibold text-base">{account.role}</p>
                    <p className="text-xs text-white/70 font-normal">{account.description}</p>
                  </div>
                </Button>
              );
            })}
          </div>

          <p className="text-xs text-gray-400 text-center mt-8">
            Mode test — connexion automatique
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div 
        className="hidden md:block md:w-1/2 relative bg-cover bg-center"
        style={{ 
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/f358f426-fef2-4075-a729-f19ff49026fb/images/529569a778bdea063fe07a8ee4be73d16b0e2e1739b85611d5afbad2197caa48.png')`
        }}
      >
        <div className="absolute inset-0 bg-white/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a2634]/70 via-transparent to-white/30" />
        <div className="absolute bottom-0 left-0 right-0 p-12 text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Groupe Verbaere
          </h2>
          <p className="text-lg text-gray-300">
            Gestion de vos conges
          </p>
        </div>
      </div>
    </div>
  );
}
